// Direct file import: the `@modules/documents` barrel pulls the whole module graph in for one
// frozen object, which crashes on circular init and makes this helper impossible to test alone.
import { DOCUMENT_HTML_ATTRIBUTES } from '@modules/documents/constants';

/**
 * Finds an element by attribute, WITHIN `container`.
 *
 * The plain-`id` branch used to call `document.getElementById`, ignoring the container. That was
 * indistinguishable while a document's content existed in exactly one place. It no longer does: a
 * document loaded in parts renders everything outside the live part as static html, with the same
 * ids, so a global lookup returns the static copy of a node the caller asked for inside the editor.
 * Every caller then treats it as editor content - `posAtDOM` answers -1 for it, and
 * `TextSelection.create` throws "Position -1 out of range".
 */
export const findElementByAttribute = (
  attribute: string,
  value: string,
  container: HTMLElement
): HTMLElement | null => {
  if (attribute.startsWith('data-')) {
    return container.querySelector(`[${attribute}="${CSS.escape(value)}"]`) as HTMLElement | null;
  } else if (attribute === DOCUMENT_HTML_ATTRIBUTES.DEFAULT_ID) {
    return container.querySelector(`#${CSS.escape(value)}`) as HTMLElement | null;
  }
  return null;
};

/**
 * Calculates how far we need to scroll to bring `childRect` near the top of the scrollable container.
 * Optionally subtracts a custom offset (like a buffer).
 */
export const calculateScrollOffset = (
  childRect: DOMRect,
  containerRect: DOMRect,
  containerScrollTop: number,
  offset: number
): number => {
  return childRect.top - containerRect.top + containerScrollTop - offset;
};
